/**
 * Спокойный порядок ленты.
 *
 * Жалоба владельца (12.08): «не нравится, что прыгает лента». Дёргается не
 * число в строке — дёргается САМА СТРОКА: глаз ведёт к вилке, а на месте уже
 * соседняя. Причина не в частоте дельт, а в том, что порядок пересобирался
 * заново на каждый кадр по точному значению ключа. Профит 214 ₽ и 216 ₽ — это
 * для человека одна и та же вилка, но для `sort` это повод поменять их
 * местами, и обмен происходил по несколько раз в секунду.
 *
 * Отсюда две меры, обе без единой миллисекунды задержки — вилка появляется на
 * экране так же быстро, как раньше.
 *
 * 1. **Сетка вместо точного значения.** Ключ сортировки округляется до шага,
 *    внутри которого разница человеку не нужна: 4 % по деньгам, 0.2 п.п. по
 *    проценту, четыре секунды по возрасту. Пока вилка гуляет внутри своей
 *    клетки, она стоит на месте; вышла за клетку — переезжает, и переезд
 *    означает настоящую подвижку цены.
 * 2. **Память мест.** Внутри клетки порядок берётся из прошлого кадра, а не
 *    из порядка обхода `Map`, который у нас меняется от переподключений и
 *    патчей. Новая строка встаёт на своё честное место сразу — её место
 *    считается по точному значению, не «в конец».
 *
 * Чем платим: две вилки, различающиеся на 3 % денег, могут стоять «не по
 * росту». Это осознанный обмен — глазу три процента не видны, а прыжок виден
 * всегда. Порядок при этом остаётся детерминированным: сетка монотонна, так
 * что сравнение транзитивно и один и тот же кадр всегда даёт один и тот же
 * список (нетранзитивный компаратор дал бы порядок, зависящий от алгоритма
 * сортировки, — то есть тот же прыжок, но необъяснимый).
 *
 * Недоверие (`distrust`) сеткой НЕ сглаживается: «цена неверна» — не оттенок
 * величины, а другой смысл строки, и тонуть такая строка обязана сразу.
 */

import type { FeedRow } from './rows';

/** Склейка всплеска: `push` принимает состояния, `pour` получает те, что дошли. */
export interface Paced<T> {
  push(value: T): void;
  /** Снять отложенный кадр: экран уходит, применять его некому. */
  stop(): void;
  /**
   * Вкладка ушла. Кадры копятся, экран не трогаем: браузер в фоне режет таймеры,
   * и ведущий фронт тогда льёт каждый кадр — лента пересобирается сотни раз,
   * пока человек ставит на чужом сайте, а по возврату вкладка чёрная.
   */
  hold(): void;
  /**
   * Вкладка вернулась. Один последний кадр сразу. `true` — кадр был, `now`
   * уже свежий; тик возраста тогда не нужен.
   */
  wake(): boolean;
}

/**
 * Склеить всплеск обновлений в один кадр.
 *
 * Ведущий фронт обязателен, и это главное отличие от привычного `debounce`.
 * Первое обновление после тишины применяется СРАЗУ, без единой миллисекунды
 * задержки: на ленте вилок задержать нечего — новая вилка и есть весь смысл
 * экрана. Склеиваются только те обновления, что пришли плотнее, чем раз в
 * `ms`, и склейка ничего не теряет: до `pour` доходит последнее состояние, а
 * оно полное — дельты сведены раньше, в клиенте сокета.
 *
 * Отложенное состояние заменяется, а не копится: очередь кадров экрану не
 * нужна, ему нужен последний.
 *
 * Пока вкладка скрыта (`hold`), ведущий фронт молчит: Chrome режет `setTimeout`
 * до секунды и больше, `since >= ms` становится правдой на каждое сообщение, и
 * лента пересобирается вхолостую. По `wake` до экрана доходит одно последнее
 * состояние — то же обещание «ничего не теряем», без очереди кадров.
 */
export function pace<T>(ms: number, pour: (value: T) => void): Paced<T> {
  let waiting: { value: T } | null = null;
  let armed: ReturnType<typeof setTimeout> | null = null;
  let pouredAt = 0;
  let held = false;
  const now = (): number => Date.now();
  const send = (value: T): void => {
    pouredAt = now();
    pour(value);
  };
  const disarm = (): void => {
    if (armed !== null) clearTimeout(armed);
    armed = null;
  };
  const flush = (): boolean => {
    disarm();
    const last = waiting;
    waiting = null;
    if (last === null) return false;
    send(last.value);
    return true;
  };
  return {
    push(value: T): void {
      if (held) {
        waiting = { value };
        return;
      }
      const since = now() - pouredAt;
      if (since >= ms && armed === null) {
        send(value);
        return;
      }
      waiting = { value };
      if (armed !== null) return;
      armed = setTimeout(() => {
        armed = null;
        const last = waiting;
        waiting = null;
        if (last === null) return;
        // Ушли в фон, пока таймер догорал: кадр подождёт `wake`, а не влезет
        // в скрытую вкладку, где его всё равно не видно.
        if (held) {
          waiting = last;
          return;
        }
        send(last.value);
      }, Math.max(0, ms - since));
    },
    stop(): void {
      disarm();
      waiting = null;
      held = false;
    },
    hold(): void {
      held = true;
      disarm();
    },
    wake(): boolean {
      held = false;
      return flush();
    },
  };
}

/** Шаг сетки по деньгам — 4 % от величины: 214 ₽ и 216 ₽ живут в одной клетке. */
const MONEY_STEP = 1.04;
/** Шаг по проценту, п.п. */
const ROI_STEP = 0.2;
/** Шаг по возрасту и живучести, с. */
const AGE_STEP = 4;
const LIFE_STEP = 5;

const LOG_STEP = Math.log(MONEY_STEP);

/**
 * Номер клетки. Меньше — выше в ленте, чтобы номер подставлялся в компаратор
 * как есть (`a - b`).
 *
 * Деньги идут по логарифмической сетке, а не по постоянному шагу: в ленте
 * рядом живут 40 ₽ и 4000 ₽, и постоянные 25 ₽ были бы для первой всей её
 * величиной, а для второй — незаметной пылью.
 */
export function cell(row: FeedRow, sort: 'money' | 'roi' | 'age' | 'life'): number {
  switch (sort) {
    case 'roi':
      return -Math.floor(row.row.roiPct / ROI_STEP);
    case 'age':
      return Math.floor(row.age / AGE_STEP);
    case 'life':
      // Неизмеренная живучесть уходит вниз одной клеткой, а не считается нулём.
      return row.lifespan === null ? Number.MAX_SAFE_INTEGER : -Math.floor(row.lifespan / LIFE_STEP);
    default: {
      const profit = row.row.profit;
      return profit > 0 ? -Math.floor(Math.log(profit) / LOG_STEP) : Number.MAX_SAFE_INTEGER;
    }
  }
}

/**
 * Места прошлого кадра. Модульная память, как кэш расчёта в `rows.ts`: список
 * один на экран, и таскать его через пропсы значило бы протянуть состояние
 * порядка через все слои сита ради одного числа на строку.
 */
let places = new Map<string, number>();

/** Забыть места — для тестов и для смены экрана, где прошлый порядок бессмыслен. */
export function forgetPlaces(): void {
  places = new Map();
}

/** Место строки в прошлом кадре. `undefined` — строка новая. */
export const placeOf = (id: string): number | undefined => places.get(id);

/** Запомнить порядок кадра. Зовётся и на замороженном кадре: место, на котором
 * строка стояла под курсором, — то же место, с которого лента продолжит после
 * оттаивания. */
export function rememberPlaces(rows: readonly FeedRow[]): void {
  const next = new Map<string, number>();
  rows.forEach((row, at) => next.set(row.id, at));
  places = next;
}

/**
 * Упорядочить кадр спокойно.
 *
 * Ключ сравнения — лексикографическая цепочка из пяти звеньев, и порядок
 * звеньев здесь и есть весь смысл модуля.
 *
 * 1. `layer` — то, что сеткой не сглаживается и старше величины: взятая в
 *    работу строка и недоверие к цене. Без него клетка подняла бы мёртвую
 *    вилку наверх, потому что денег в ней «много».
 * 2. Клетка — величина, округлённая до шага, внутри которого разница человеку
 *    не нужна.
 * 3. Прежнее место. Новой строке места нет, и она встаёт в НАЧАЛО своей
 *    клетки: внутри клетки величины и так равны с точностью до шага, а
 *    свежесть — ценность. Ставить её в конец значило бы прятать новую вилку за
 *    теми, кто её не лучше.
 * 4. `hard` — честное сравнение по точному значению. Работает там, где память
 *    молчит: между двумя новичками одной клетки.
 * 5. `id` — чтобы порядок не зависел от обхода `Map`, который у нас меняется
 *    от переподключений и патчей.
 *
 * Каждое звено — число или транзитивное сравнение, поэтому цепочка транзитивна
 * целиком: один и тот же кадр всегда даёт один и тот же список.
 */
export function settle(
  rows: readonly FeedRow[],
  sort: 'money' | 'roi' | 'age' | 'life',
  hard: (a: FeedRow, b: FeedRow) => number,
  layer: (row: FeedRow) => number = () => 0,
): FeedRow[] {
  const place = (row: FeedRow): number => places.get(row.id) ?? -1;
  // Память пишет тот, кто знает итоговый кадр целиком (`sift` в конце): здесь
  // список ещё может лишиться строк на свёртке повторов одного матча.
  return [...rows].sort(
    (a, b) =>
      layer(a) - layer(b) ||
      cell(a, sort) - cell(b, sort) ||
      place(a) - place(b) ||
      hard(a, b) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}
